import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import { triggerUpdate, triggerRestart, runCliCommand, type CliCommandName, type CliRunResult } from '../api/about';
import { fetchNextWpId } from '../api/dev';
import { UpdateDialog, RestartDialog } from '../components/admin/UpdateDialogs';
import { usePermission } from '../hooks/usePermission';
import { useSnackbar } from '../context/SnackbarContext';

// ── CLI palette ────────────────────────────────────────────────────────────

interface CliEntry {
  id: CliCommandName | 'update' | 'restart' | 'next-id';
  label: string;
  description: string;
  destructive?: boolean;
}

const CLI_ENTRIES: CliEntry[] = [
  { id: 'status',  label: 'webadmin status',  description: 'Show systemd service status' },
  { id: 'logs',    label: 'webadmin logs',    description: 'Last 100 lines of backend journal' },
  { id: 'restart', label: 'webadmin restart', description: 'Restart service (no rebuild)', destructive: true },
  { id: 'update',  label: 'webadmin update',  description: 'Pull, rebuild and restart',   destructive: true },
  { id: 'next-id', label: 'wp next-id',       description: 'Next available work package ID' },
];

interface CliPaletteProps {
  onUpdate: () => void;
  onRestart: () => void;
}

function CliPalette({ onUpdate, onRestart }: CliPaletteProps) {
  const [running, setRunning] = useState<string | null>(null);
  const [result, setResult] = useState<(CliRunResult & { cmd: string }) | null>(null);

  const run = async (entry: CliEntry) => {
    if (running) return;
    if (entry.id === 'update') { onUpdate(); return; }
    if (entry.id === 'restart') { onRestart(); return; }
    if (entry.id === 'next-id') {
      setRunning(entry.id);
      setResult(null);
      try {
        const res = await fetchNextWpId();
        setResult({ output: JSON.stringify(res, null, 2), exit_code: 0, cmd: entry.label });
      } finally {
        setRunning(null);
      }
      return;
    }
    setRunning(entry.id);
    setResult(null);
    try {
      const res = await runCliCommand(entry.id);
      setResult({ ...res, cmd: entry.label });
    } finally {
      setRunning(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <TerminalIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: '0.65rem', fontWeight: 600 }}>
          Implementing
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={result ? 2 : 0}>
        {CLI_ENTRIES.map((entry) => (
          <Button
            key={entry.id}
            size="small"
            variant="outlined"
            color={entry.destructive ? 'warning' : 'inherit'}
            disabled={!!running}
            startIcon={running === entry.id ? <CircularProgress size={13} /> : undefined}
            onClick={() => void run(entry)}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              textTransform: 'none',
              borderColor: 'divider',
              color: entry.destructive ? 'warning.light' : 'text.primary',
              '&:hover': { borderColor: entry.destructive ? 'warning.main' : 'primary.main' },
            }}
            title={entry.description}
          >
            {entry.label}
          </Button>
        ))}
      </Stack>

      {result && (
        <Box
          sx={{
            mt: 1.5,
            bgcolor: '#0d1117',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} px={1.5} py={0.75} sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.03)' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: result.exit_code === 0 ? 'success.main' : 'error.main' }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.72rem' }}>
              $ {result.cmd}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', fontSize: '0.68rem' }}>
              exit {result.exit_code}
            </Typography>
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              fontFamily: 'monospace',
              fontSize: '0.72rem',
              lineHeight: 1.6,
              color: result.exit_code === 0 ? '#e6edf3' : '#ff7b72',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 340,
              overflowY: 'auto',
            }}
          >
            {result.output || '(no output)'}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function Debugging() {
  const canManage = usePermission('system.manage');
  const showSnackbar = useSnackbar();
  const [updating, setUpdating] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const handleUpdate = async () => {
    try {
      await triggerUpdate();
      setUpdating(true);
    } catch {
      showSnackbar('Failed to start update', 'error');
    }
  };

  const handleRestart = async () => {
    try {
      await triggerRestart();
      setRestarting(true);
    } catch {
      showSnackbar('Failed to start restart', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Debugging</Typography>
          <Typography variant="body2" color="text.secondary">CLI commands and service control</Typography>
        </Box>
      </Stack>

      {canManage && (
        <Paper sx={{ p: 2.5 }}>
          <CliPalette
            onUpdate={() => void handleUpdate()}
            onRestart={() => void handleRestart()}
          />
        </Paper>
      )}

      <UpdateDialog open={updating} onDone={() => { window.location.reload(); }} />
      <RestartDialog open={restarting} onDone={() => { window.location.reload(); }} />
    </Box>
  );
}
